import Image from 'next/image';
import { memo, useEffect, useRef, useState } from 'react';
import { useThumbnail } from './thumbnailContext';

interface SlideBackgroundProps {
  bgImg: string;
  bgImgOpacity: number;
  priority?: boolean;
  thumbnail?: boolean;
  isHovered?: boolean;
}

const VIDEO_EXTENSIONS = ['mp4', 'webm'];

const posterCache = new Map<string, string | null>();

function VideoBackground({ src, bgImg, posterSrcs, style }: { src: string; bgImg: string; posterSrcs: string[]; style: React.CSSProperties }) {
  const poster = posterSrcs[0];

  return (
    <div className="absolute inset-0 overflow-hidden w-full h-full" style={{ contain: 'strict' }}>
      <video
        key={bgImg}
        style={{ ...style, willChange: 'transform' }}
        src={src}
        {...(poster ? { poster } : {})}
        className="size-full object-cover"
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        disablePictureInPicture
      />
    </div>
  );
}

function ThumbnailStaticBackground({ src, bgImg, style, priority }: { src: string; bgImg: string; style: React.CSSProperties; priority: boolean }) {
  const thumbnailSrc = src.replace(/\.[^.]+$/, '-thumbnail.webp');
  const [imgSrc, setImgSrc] = useState(thumbnailSrc);

  return (
    <Image
      key={bgImg}
      style={style}
      src={imgSrc}
      className="object-cover"
      fill
      sizes="320px"
      quality={20}
      priority={priority}
      referrerPolicy="no-referrer"
      alt="background image"
      onError={() => { if (imgSrc !== src) setImgSrc(src); }}
    />
  );
}

function ThumbnailVideoBackground({ src, bgImg, posterSrcs, style, priority, isHovered = false }: { src: string; bgImg: string; posterSrcs: string[]; style: React.CSSProperties; priority: boolean; isHovered?: boolean }) {
  const cached = posterCache.get(src);
  const [resolvedPoster, setResolvedPoster] = useState<string | null | undefined>(cached !== undefined ? cached : undefined);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const url of posterSrcs) {
        // Use a DOM Image probe instead of fetch — browser image load failures
        // don't surface in PostHog network capture, and a successful load warms
        // the browser cache before the <Image> component renders.
        const ok = await new Promise<boolean>(resolve => {
          const img = new window.Image();
          img.onload = () => resolve(true);
          img.onerror = () => resolve(false);
          img.src = url;
        });
        if (cancelled) return;
        if (ok) { setResolvedPoster(url); return; }
      }
      if (!cancelled) setResolvedPoster(null);
    })();
    return () => { cancelled = true; };
  }, [posterSrcs]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || resolvedPoster !== null) return;
    if (isHovered) {
      video.currentTime = 0;
      video.play().catch(() => { });
    } else {
      video.pause();
      video.currentTime = 0;
    }
  }, [isHovered, resolvedPoster]);

  if (resolvedPoster === undefined) return null;

  if (resolvedPoster) {
    return (
      <div className="absolute inset-0">
        {isHovered ? (
          <div className="absolute inset-0 overflow-hidden" style={{ contain: 'strict' }}>
            <video
              key={bgImg}
              style={{ ...style, willChange: 'transform' }}
              src={src}
              poster={resolvedPoster}
              className="size-full object-cover"
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              disablePictureInPicture
            />
          </div>
        ) : (
          <Image
            key={bgImg}
            style={style}
            src={resolvedPoster}
            className="object-cover"
            fill
            sizes="320px"
            quality={20}
            priority={priority}
            referrerPolicy="no-referrer"
            alt="background image"
          />
        )}
      </div>
    );
  }

  return (
    <div className="absolute inset-0 overflow-hidden" style={{ contain: 'strict' }}>
      <video
        ref={videoRef}
        key={bgImg}
        style={style}
        src={`${src}#t=0.001`}
        className="size-full object-cover"
        muted
        playsInline
        preload="metadata"
        disablePictureInPicture
      />
    </div>
  );
}

export default memo(function SlideBackground({ bgImg, bgImgOpacity, priority = false, thumbnail = false, isHovered }: SlideBackgroundProps) {
  const isThumbnail = thumbnail || useThumbnail();
  const ext = bgImg.split('.').pop()?.toLowerCase() || '';
  const src = `${process.env.NEXT_PUBLIC_BUCKET_URL}${bgImg}`;
  const style = { opacity: bgImgOpacity };

  if (VIDEO_EXTENSIONS.includes(ext)) {
    const posterBase = src.replace(/\.[^.]+$/, '');
    const posterSrcs = isThumbnail
      ? [`${posterBase}-thumbnail.webp`, `${posterBase}.webp`, `${posterBase}.jpeg`]
      : [`${posterBase}.webp`, `${posterBase}.jpeg`];

    if (!isThumbnail) {
      return <VideoBackground src={src} bgImg={bgImg} posterSrcs={posterSrcs} style={style} />;
    }
    return <ThumbnailVideoBackground src={src} bgImg={bgImg} posterSrcs={posterSrcs} style={style} priority={priority} isHovered={isHovered} />;
  }

  if (isThumbnail) {
    return <ThumbnailStaticBackground src={src} bgImg={bgImg} style={style} priority={priority} />;
  }

  return (
    <Image
      key={bgImg}
      style={style}
      src={src}
      className="object-cover"
      fill
      sizes="(max-width: 768px) 100vw, 1280px"
      quality={85}
      priority={priority}
      referrerPolicy="no-referrer"
      alt="background image"
    />
  );
});
